import { useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { usePageLang } from '@/hooks/usePageLang';

interface PageShellProps {
  eyebrow: string;
  title: string;
  intro: string;
  children: ReactNode;
}

export default function PageShell({ eyebrow, title, intro, children }: PageShellProps) {
  const { t } = useTranslation('pages');
  const { currentLang, handleLanguageChange } = usePageLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar currentLang={currentLang} onLanguageChange={handleLanguageChange} solid />

      <main className="pt-16 lg:pt-18">
        {/* Header */}
        <header className="bg-background-100 border-b border-background-200/40">
          <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16 max-w-5xl mx-auto py-10 md:py-16">
            <nav className="flex items-center gap-2 text-sm mb-5" aria-label="Breadcrumb">
              <Link to={`/${currentLang}/`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                {t('page_breadcrumb_home')}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300 text-xs"></i>
              <span className="text-foreground-700 font-medium">{title}</span>
            </nav>
            <p className="text-accent-500 text-xs font-semibold uppercase tracking-[0.15em] mb-3">
              {eyebrow}
            </p>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900 leading-snug mb-5">
              {title}
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-3xl">
              {intro}
            </p>
          </div>
        </header>

        {/* Body */}
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16 max-w-5xl mx-auto py-10 md:py-16">
          {children}

          {/* CTA */}
          <div className="bg-primary-50 rounded-xl p-6 md:p-8 border border-primary-200/40 mt-12">
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
              {t('page_cta_title')}
            </h2>
            <p className="text-foreground-600 text-base leading-relaxed mb-5">
              {t('page_cta_text')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-label font-semibold text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('page_cta_button')}
                <i className="ri-external-link-line"></i>
              </a>
              <Link
                to={`/${currentLang}/`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-background-300/60 text-foreground-700 font-label font-semibold text-sm rounded-md hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                {t('page_back_home')}
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer currentLang={currentLang} />
    </div>
  );
}

export function useShellLang() {
  return usePageLang();
}